import { useMutation } from "@tanstack/react-query";
import { authService, userService } from "services";
import {
  EModelAuth,
  FormLogin,
  FormLoginSocial,
  FormRegister,
  MAPPING_ERROR,
  OTPForm,
  TypeVerify,
} from "types";
import { useKey } from "./useKey";
import { useUser } from "./useUser";
import { useLayout } from "./useLayout";
import SnackbarUtils from "utils/SnackbarUtils";
import { useNavigate } from "react-router-dom";
import { PRIVATE_ROOT, PUBLIC_URL } from "utils/routes";

export const useAuthentication = () => {
  const { setKeySite, removeKeySite } = useKey();
  const { setUser, setIsLogged, logout } = useUser();
  const { setOpenModalAuth } = useLayout();
  const navigate = useNavigate();

  const onLogin = (formData: FormLogin) => {
    const { email, password } = formData

    if (email === "admin" && password === "password") {
      const data = {
        "data": {
          "accessToken": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1aWQiOiI2ODIyZjI0NTNmNjQ4MDg1ZmU0YWY1ZDEiLCJlbWFpbCI6ImFkbWluIiwiaWF0IjoxNzQ3ODE4MjAxLCJleHAiOjE3NDc5MDQ2MDF9.0ikEKe_E-hSSm6PYGdOqo6-G6_tBYLqQex_6jakigN4",
          "refreshToken": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1aWQiOiI2ODIyZjI0NTNmNjQ4MDg1ZmU0YWY1ZDEiLCJlbWFpbCI6ImFkbWluIiwiaWF0IjoxNzQ3ODE4MjAxLCJleHAiOjE3NDc5OTEwMDF9.WEJGQLiI9ED2DSbHHm42YSK6lnBDErZI1Y1eTJIYoGk"
        },
        "message": "OK",
        "statusCode": 200
      }
      if (data?.statusCode === 200) {
        const { accessToken, refreshToken } = data.data;
        setKeySite({ authToken: accessToken, refreshToken: refreshToken });
        setIsLogged(true);
        navigate(PRIVATE_ROOT.DASHBOARD);

        return;
      }
    }
    SnackbarUtils.warning("Login failed");

  }
  const loginMutation = useMutation({
    mutationFn: (formData: FormLogin) => authService.login(formData),
    onSuccess: async ({ data }) => {

      if (data?.statusCode === 200) {
        const { accessToken, refreshToken } = data.data;
        setKeySite({ authToken: accessToken, refreshToken: refreshToken });
        setIsLogged(true);
        // navigate(PRIVATE_ROOT.DASHBOARD);

        return;
      }
      SnackbarUtils.warning(data?.message || "Login failed");
    },
    onError: (error: any) => {
      SnackbarUtils.warning(MAPPING_ERROR[error.data?.detail]);
    },
  });

  const loginSocialMutation = useMutation({
    mutationFn: (formData: FormLoginSocial) =>
      authService.loginGoogle(formData),
    onSuccess: async ({ data }) => {
      if (data?.statusCode === 200) {
        const { accessToken, refreshToken } = data.data;
        setKeySite({ authToken: accessToken, refreshToken: refreshToken });
        const rs = await userService.getProfile();
        if (rs?.data) {
          setOpenModalAuth(EModelAuth.DEFAULT);
          setUser(rs.data?.data);
          setIsLogged(true);
        }
        return;
      }
      SnackbarUtils.warning(data?.message || "Login failed");
    },
    onError: () => {
      SnackbarUtils.warning("Login failed!");
    },
  });

  const registerMutation = useMutation({
    mutationFn: (formData: FormRegister) => authService.register(formData),
    onSuccess: async ({ data }) => {
      if (data?.statusCode === 200) {
        SnackbarUtils.success("Register success!");
        navigate(PUBLIC_URL.LOGIN);
        return;
      }
      SnackbarUtils.warning(data?.message || "Register failed");
    },
    onError: (error) => {
      console.log("🚀 ~ SignIn ~ error:", error);
      SnackbarUtils.warning("Login failed!");
    },
  });

  const resetStore = () => {
    setIsLogged(false);
    removeKeySite();
    localStorage.clear();
    logout();
    return;
  };

  const logoutMutation = useMutation({
    mutationFn: () => userService.logout(),
    onSuccess: async ({ data }) => {
      if (data.code === 200) {
        resetStore();
      }
    },
    onError: (error) => {
      resetStore();
    },
  });

  const verifyEmailMutation = useMutation({
    mutationFn: ({ email, mode }: { email: string; mode: TypeVerify }) =>
      authService.verifyEmail(email, mode),
    onSuccess: async (data) => {
      if (data?.statusCode === 200) return;
      SnackbarUtils.warning(data?.message || "Register failed");
    },
    onError: (error: any) => {
      SnackbarUtils.warning(MAPPING_ERROR[error.data?.detail]);
    },
  });

  const verifyOTPlMutation = useMutation({
    mutationFn: (data: OTPForm) =>
      authService.verifyOtp(data.email, data.type, data.code),
    onSuccess: async (data) => {
      if (data?.statusCode === 200) {
        const { access_token } = data.data;
        setIsLogged(true);
        navigate(PRIVATE_ROOT.DASHBOARD);
        setKeySite({ authToken: access_token });
        const rs = await userService.getProfile();
        if (rs?.data) {
          setUser(rs.data?.data);
          setIsLogged(true);
        }
        navigate(PRIVATE_ROOT.DASHBOARD);
        SnackbarUtils.success("Login success!");
        return;
      }
      SnackbarUtils.warning(data?.message || "Verify failed");
    },
    onError: (error: any) => {
      SnackbarUtils.warning(MAPPING_ERROR[error.data?.detail]);
    },
  });

  return {
    onLogin,
    resetStore,
    loginMutation,
    registerMutation,
    logoutMutation,
    loginSocialMutation,
    verifyEmailMutation,
    verifyOTPlMutation,
  };
};
